import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './AdminPanel.css';
import API_URL, { parseJsonResponse, verifyPortalApi } from '../api';
import { normalizeEmployeeId } from '../utils/qrHelpers';
import EmployeeQRModal from './EmployeeQRModal';

async function loadPortalAccounts(apiUrl) {
  const getResponse = await fetch(`${apiUrl}?action=get_employee_portals`);
  let data = await parseJsonResponse(getResponse);

  if (Array.isArray(data?.accounts)) {
    return { accounts: data.accounts, error: '' };
  }

  if (!getResponse.ok) {
    const postResponse = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'list_employee_portals' }),
    });
    data = await parseJsonResponse(postResponse);
    if (Array.isArray(data?.accounts)) {
      return { accounts: data.accounts, error: '' };
    }
  }

  return {
    accounts: [],
    error: data?.message || 'Hindi ma-load ang employee portal accounts. I-upload ulit ang bagong api.php sa server.',
  };
}

async function resolvePortalApiUrl() {
  const mainCheck = await verifyPortalApi(API_URL);
  if (mainCheck.ok) {
    return { url: API_URL, ready: true, message: '' };
  }

  return {
    url: API_URL,
    ready: false,
    message: mainCheck.message || 'upload_required',
  };
}

function EmployeePortalAccounts({ registeredEmployees = [], onRefreshEmployees }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [portalUsername, setPortalUsername] = useState('');
  const [portalPassword, setPortalPassword] = useState('Pass@123');
  const [editingId, setEditingId] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [portalApiUrl, setPortalApiUrl] = useState(API_URL);
  const [portalReady, setPortalReady] = useState(false);
  const [portalNotice, setPortalNotice] = useState('');
  const [loadError, setLoadError] = useState('');
  const [qrModalEmployee, setQrModalEmployee] = useState(null);

  useEffect(() => {
    let cancelled = false;
    resolvePortalApiUrl().then((result) => {
      if (cancelled) return;
      setPortalApiUrl(result.url);
      setPortalReady(result.ready);
      setPortalNotice(result.ready ? '' : 'upload_required');
    });
    return () => { cancelled = true; };
  }, []);

  const fetchAccounts = useCallback(async () => {
    if (!portalReady) return;
    try {
      const result = await loadPortalAccounts(portalApiUrl);
      setAccounts(result.accounts);
      setLoadError(result.error);
    } catch (error) {
      console.error('Error fetching employee portal accounts:', error);
      setAccounts([]);
      setLoadError('Hindi makakonekta sa server para i-load ang portal accounts.');
    }
  }, [portalApiUrl, portalReady]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  useEffect(() => {
    onRefreshEmployees?.();
  }, [onRefreshEmployees]);

  const employees = useMemo(() => {
    const byUid = new Map();
    (registeredEmployees || []).forEach((emp) => {
      const uid = normalizeEmployeeId(emp.employeeId);
      if (!uid) return;
      if (!byUid.has(uid)) {
        byUid.set(uid, emp);
      }
    });
    return Array.from(byUid.values()).sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''))
    );
  }, [registeredEmployees]);

  const portalAccountUids = useMemo(() => new Set(
    accounts.map((acc) => normalizeEmployeeId(acc.employeeId))
  ), [accounts]);

  const employeesWithoutAccount = useMemo(() => (
    employees.filter((emp) => !portalAccountUids.has(normalizeEmployeeId(emp.employeeId)))
  ), [employees, portalAccountUids]);

  const handleEmployeeSelect = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    const emp = employees.find(
      (e) => normalizeEmployeeId(e.employeeId) === normalizeEmployeeId(employeeId)
    );
    setPortalUsername(emp?.eid || '');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!portalReady) {
      alert(portalNotice || 'Hindi pa handa ang employee portal API sa server.');
      return;
    }
    if (!selectedEmployeeId || !portalUsername || !portalPassword) {
      alert('Kumpletuhin ang employee, username, at password.');
      return;
    }

    try {
      const response = await fetch(portalApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'register_employee_portal',
          employeeId: selectedEmployeeId,
          username: portalUsername,
          password: portalPassword,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!data) {
        alert('Walang valid response mula sa server. I-upload ang employee_portal.php sa helportattendance.com.');
        return;
      }
      if (data.status === 'success') {
        alert('Employee portal account created!');
        setSelectedEmployeeId('');
        setPortalUsername('');
        setPortalPassword('Pass@123');
        await fetchAccounts();
        onRefreshEmployees?.();
      } else {
        alert(data.message || 'Failed to create account.');
      }
    } catch (error) {
      console.error('Create portal account error:', error);
      alert('Hindi makakonekta sa server. I-upload ang employee_portal.php sa helportattendance.com.');
    }
  };

  const handleDelete = async (id, name) => {
    if (!portalReady) return;
    if (!window.confirm(`Burahin ang portal account ni ${name}?`)) return;
    try {
      const response = await fetch(portalApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_employee_portal', id }),
      });
      const data = await parseJsonResponse(response);
      if (data?.status === 'success') {
        fetchAccounts();
        alert('Portal account deleted.');
      } else {
        alert(data?.message || 'Delete failed.');
      }
    } catch (error) {
      alert('Server error.');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditUsername(row.username);
    setEditPassword('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!portalReady) return;
    try {
      const response = await fetch(portalApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update_employee_portal',
          id: editingId,
          username: editUsername,
          password: editPassword,
        }),
      });
      const data = await parseJsonResponse(response);
      if (data?.status === 'success') {
        setEditingId(null);
        fetchAccounts();
        alert('Portal account updated!');
      } else {
        alert(data?.message || 'Update failed.');
      }
    } catch (error) {
      alert('Server error.');
    }
  };

  const formatEmployeeOption = (emp) => {
    const eid = emp.eid ? String(emp.eid).trim() : 'No EID';
    return `${emp.name} — EID: ${eid}`;
  };

  const openQrForAccount = (row) => {
    const match = employees.find(
      (emp) => normalizeEmployeeId(emp.employeeId) === normalizeEmployeeId(row.employeeId)
    );
    setQrModalEmployee(match || {
      employeeId: row.employeeId,
      name: row.name,
      eid: row.eid,
    });
  };

  return (
    <div className="admin-panel admin-panel--wide">
      <div className="admin-panel-header">
        <div className="admin-panel-header-left">
          <div className="admin-panel-badge">
            <span className="admin-panel-badge-dot" />
            Portal
          </div>
          <h2 className="admin-panel-title">Employee Portal Accounts</h2>
          <p className="admin-panel-subtitle">
            Employees can log in to view their own attendance logs only.
          </p>
        </div>
      </div>

      {portalNotice && (
        <div className="api-status-banner portal-setup-banner" role="alert">
          <strong>I-upload ang bagong api.php sa server:</strong>
          <p style={{ margin: '0.5rem 0' }}>
            Tama na ang code sa computer mo. Ang problema: <strong>hindi pa na-replace ang file sa helportattendance.com</strong>.
            Kapag tinest ang server, logs pa rin ang lumalabas sa halip na health/accounts.
          </p>
          <ol className="portal-setup-steps">
            <li>cPanel → File Manager → <strong>public_html</strong></li>
            <li>I-rename ang lumang <code>api.php</code> → <code>api_old.php</code> (backup)</li>
            <li>I-upload ang bagong <code>api.php</code> mula sa <code>rfid-system/backend/</code></li>
            <li>Optional: i-upload din ang <code>bootstrap_accounts.php</code> (para sa MEGELIN)</li>
            <li>Test: <code>https://helportattendance.com/api.php?action=health</code></li>
            <li>Dapat lumabas: <code>{'{"system":"rfid_attendance","version":"2.0"}'}</code></li>
            <li>I-refresh ang page na ito</li>
          </ol>
        </div>
      )}

      {loadError && (
        <div className="api-status-banner" role="alert">
          {loadError}
        </div>
      )}

      <div className="admin-form-wrapper" style={{ marginBottom: 24 }}>
        <div className="admin-form-panel" style={{ maxWidth: 720 }}>
          <h3 className="admin-form-title" style={{ fontSize: '1.1rem' }}>Create Employee Account</h3>
          <form onSubmit={handleCreate}>
            <div className="admin-form-row">
              <div className="admin-form-col">
                <label className="admin-form-label">Employee</label>
                <select
                  className="admin-form-input"
                  value={selectedEmployeeId}
                  onChange={(e) => handleEmployeeSelect(e.target.value)}
                  required
                  disabled={!portalReady}
                >
                  <option value="">-- Select Employee --</option>
                  {employeesWithoutAccount.length === 0 ? (
                    <option value="" disabled>
                      {employees.length === 0
                        ? 'Walang registered employee — mag-register muna sa Register Employee'
                        : 'Lahat ng employees ay may portal account na'}
                    </option>
                  ) : (
                    employeesWithoutAccount.map((emp) => (
                      <option key={emp.employeeId} value={emp.employeeId}>
                        {formatEmployeeOption(emp)}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="admin-form-col">
                <label className="admin-form-label">Username</label>
                <input
                  type="text"
                  className="admin-form-input"
                  value={portalUsername}
                  onChange={(e) => setPortalUsername(e.target.value)}
                  placeholder="Default: EID"
                  required
                  disabled={!portalReady}
                />
              </div>
            </div>
            <div className="admin-form-group">
              <label className="admin-form-label">Password</label>
              <input
                type="text"
                className="admin-form-input"
                value={portalPassword}
                onChange={(e) => setPortalPassword(e.target.value)}
                required
                disabled={!portalReady}
              />
            </div>
            <button type="submit" className="admin-submit-btn" disabled={!portalReady}>
              Create Portal Account
            </button>
          </form>
        </div>
      </div>

      <div className="admin-table-wrap">
        {accounts.length === 0 ? (
          <div className="admin-empty">
            <p className="admin-empty-title">No employee portal accounts yet</p>
            <p className="admin-empty-sub">Create an account so employees can view their attendance logs.</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>EID</th>
                <th>UID</th>
                <th>Username</th>
                <th>Password</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((row) => (
                <React.Fragment key={row.id}>
                  <tr>
                    <td><span className="admin-table-name">{row.name}</span></td>
                    <td><span className="admin-table-muted">{row.eid || '—'}</span></td>
                    <td><span className="admin-table-muted">{row.employeeId}</span></td>
                    <td>{row.username}</td>
                    <td><span className="admin-table-muted">••••••••</span></td>
                    <td>
                      <div className="admin-btn-group" style={{ justifyContent: 'center' }}>
                        <button type="button" onClick={() => openQrForAccount(row)} className="admin-btn admin-btn--qr">QR</button>
                        <button type="button" onClick={() => startEdit(row)} className="admin-btn admin-btn--edit">Edit</button>
                        <button type="button" onClick={() => handleDelete(row.id, row.name)} className="admin-btn admin-btn--delete">Delete</button>
                      </div>
                    </td>
                  </tr>
                  {editingId === row.id && (
                    <tr className="admin-edit-row">
                      <td colSpan="6">
                        <form onSubmit={handleUpdate} className="admin-edit-form">
                          <input
                            type="text"
                            className="admin-form-input"
                            style={{ flex: 1, minWidth: 140 }}
                            placeholder="Username"
                            value={editUsername}
                            onChange={(e) => setEditUsername(e.target.value)}
                            required
                          />
                          <input
                            type="password"
                            className="admin-form-input"
                            style={{ flex: 1, minWidth: 140 }}
                            placeholder="New Password (optional)"
                            value={editPassword}
                            onChange={(e) => setEditPassword(e.target.value)}
                          />
                          <button type="submit" className="admin-btn admin-btn--save">Save</button>
                          <button type="button" onClick={() => setEditingId(null)} className="admin-btn admin-btn--cancel">Cancel</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EmployeeQRModal
        employee={qrModalEmployee}
        onClose={() => setQrModalEmployee(null)}
      />
    </div>
  );
}

export default EmployeePortalAccounts;
